/**
 * Rolagens no formato da mesa: soma com operadores, Fudge/Fate, ordenação,
 * ficar com os mais altos ou os mais baixos (`4d6a3`, `2d20b`) e modo
 * Storyteller com dificuldade. Comportamento em docs/spec/dice.md.
 *
 * O RNG é injetado: o servidor usa um gerador criptográfico e os testes usam
 * um gerador determinístico.
 */
import type { Rng } from './expression.js';

export type RolagemErrorCode =
  | 'empty'
  | 'unknownChar'
  | 'missingFace'
  | 'unknownRoll'
  | 'unknownOperator'
  | 'unknownComparator'
  | 'maxOneComparator'
  | 'minOneDie'
  | 'flood'
  | 'bigNumber';

export class RolagemError extends Error {
  override readonly name = 'RolagemError';
  constructor(
    readonly code: RolagemErrorCode,
    message: string,
    /** o caractere ou o comparador que deu problema, para a mensagem traduzida */
    readonly detail = '',
  ) {
    super(message);
  }
}

/** `a` (os mais altos) ou `b` (os mais baixos): quantos dados do grupo contam */
export type RolagemKeep = { which: 'high' | 'low'; count: number };

export type RolagemItem =
  | { kind: 'dice'; sign: 1 | -1; count: number; faces: number; keep?: RolagemKeep }
  | { kind: 'fudge'; sign: 1 | -1; count: number; keep?: RolagemKeep }
  | { kind: 'value'; sign: 1 | -1; value: number };

export type ParsedRolagem = {
  items: RolagemItem[];
  /** `ord`: mostrar os dados em ordem crescente */
  sorted: boolean;
  /** dificuldade do modo Storyteller (`> K`); null = soma normal */
  difficulty: number | null;
  /** texto depois do primeiro `:` */
  message: string;
};

/** `dropped`: os índices de `results` que ficaram de fora (`a`/`b`); ausente, todos contam */
export type RolledItem =
  | { kind: 'dice'; sign: 1 | -1; faces: number; results: number[]; dropped?: number[] }
  | { kind: 'fudge'; sign: 1 | -1; results: (-1 | 0 | 1)[]; dropped?: number[] }
  | { kind: 'value'; sign: 1 | -1; value: number };

export type StorytellerOutcome = 'criticalFailure' | 'failure' | 'success';

export type RolagemResult = {
  parsed: ParsedRolagem;
  items: RolledItem[];
  /** soma de tudo (no modo Storyteller, também é calculada) */
  total: number;
  /** só no modo Storyteller */
  storyteller: { successes: number; outcome: StorytellerOutcome } | null;
};

export const ROLAGEM_LIMITS = {
  minCount: 1,
  maxCount: 255,
  minFaces: 1,
  maxFaces: 255,
  /** dados + termos, acima disto a rolagem é recusada (flood) */
  maxUnits: 300,
  /**
   * o maior número solto (e a maior dificuldade): com 300 termos, o total cabe na coluna INT das
   * rolagens; acima, a gravação falhava e a rolagem se perdia
   */
  maxValue: 1_000_000,
} as const;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Separa `expressão: mensagem`. */
export function splitDiceAndMessage(text: string): { dice: string; message: string } {
  const i = text.indexOf(':');
  return i < 0 ? { dice: text, message: '' } : { dice: text.slice(0, i), message: text.slice(i + 1).trim() };
}

const WHITESPACE = new Set([' ', '\t', '\n', '\r', ' ']);

/**
 * Interpreta o texto de uma rolagem.
 * @param requireDie quando true, exige ao menos um dado (o `/rolar` exige).
 */
export function parseRolagem(input: string, requireDie = true): ParsedRolagem {
  const { dice, message } = splitDiceAndMessage(input);
  const text = dice.trim();
  if (!text) throw new RolagemError('empty', 'expressão de rolagem vazia');

  const items: RolagemItem[] = [];
  let sorted = false;
  let difficulty: number | null = null;
  let comparators = 0;

  // estado do termo em construção
  let sign: 1 | -1 = 1;
  let countDigits = '';
  let faceDigits = '';
  let afterD = false;
  let keep: 'high' | 'low' | null = null;
  let keepDigits = '';
  let comparator = '';
  let comparatorDigits = '';

  const inTerm = () => afterD || countDigits !== '';
  const bounded = (digits: string): number => {
    const value = Number.parseInt(digits, 10);
    if (value > ROLAGEM_LIMITS.maxValue) throw new RolagemError('bigNumber', 'número grande demais na rolagem');
    return value;
  };
  const flushTerm = () => {
    if (comparator) {
      if (comparator !== '>') throw new RolagemError('unknownComparator', `comparador desconhecido: "${comparator}"`, comparator);
      comparators++;
      if (comparators > 1) throw new RolagemError('maxOneComparator', 'só pode existir 1 comparador na rolagem');
      difficulty = bounded(comparatorDigits || '0');
      comparator = '';
      comparatorDigits = '';
      return;
    }
    if (afterD) {
      if (!faceDigits) throw new RolagemError('missingFace', 'faltou a face do dado (d20, d8, d6…)');
      const count = clamp(countDigits ? Number.parseInt(countDigits, 10) : 1, ROLAGEM_LIMITS.minCount, ROLAGEM_LIMITS.maxCount);
      const kept = keep ? { keep: { which: keep, count: clamp(keepDigits ? Number.parseInt(keepDigits, 10) : 1, 1, count) } } : {};
      if (faceDigits === 'F') items.push({ kind: 'fudge', sign, count, ...kept });
      else {
        const faces = clamp(Number.parseInt(faceDigits, 10), ROLAGEM_LIMITS.minFaces, ROLAGEM_LIMITS.maxFaces);
        items.push({ kind: 'dice', sign, count, faces, ...kept });
      }
    } else if (countDigits) {
      items.push({ kind: 'value', sign, value: bounded(countDigits) });
    }
    countDigits = '';
    faceDigits = '';
    afterD = false;
    keep = null;
    keepDigits = '';
    sign = 1;
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (WHITESPACE.has(c)) continue;
    if (comparator) {
      // depois do comparador só vêm dígitos (ou mais símbolos do próprio comparador)
      if (c >= '0' && c <= '9') {
        comparatorDigits += c;
        continue;
      }
      if (c === '>') {
        comparator += c; // ">>" vira um comparador desconhecido, como no original
        continue;
      }
      flushTerm();
    }
    if (c >= '0' && c <= '9') {
      if (keep) keepDigits += c;
      // "1dF6": o dado Fudge não tem número de faces
      else if (afterD && faceDigits === 'F') throw new RolagemError('unknownRoll', 'rolagem de dados desconhecida');
      else if (afterD) faceDigits += c;
      else countDigits += c;
    } else if ((c === 'a' || c === 'A' || c === 'b' || c === 'B') && afterD && !keep) {
      // "4d6a3": só logo depois das faces, uma vez por grupo
      if (!faceDigits) throw new RolagemError('missingFace', 'faltou a face do dado (d20, d8, d6…)');
      keep = c === 'a' || c === 'A' ? 'high' : 'low';
    } else if (c === 'd' || c === 'D') {
      if (afterD) throw new RolagemError('unknownRoll', 'rolagem de dados desconhecida');
      afterD = true;
    } else if ((c === 'f' || c === 'F') && afterD && faceDigits === '') {
      faceDigits = 'F';
    } else if (c === '+' || c === '-') {
      if (inTerm()) flushTerm();
      sign = c === '-' ? (sign === -1 ? 1 : -1) : sign;
    } else if (c === '>') {
      if (inTerm()) flushTerm();
      comparator = c;
    } else if ((c === 'o' || c === 'O') && text.slice(i, i + 3).toUpperCase() === 'ORD') {
      if (inTerm()) flushTerm();
      sorted = true;
      i += 2;
    } else {
      throw new RolagemError('unknownChar', `caractere desconhecido na rolagem: "${c}"`, c);
    }
  }
  flushTerm();

  if (requireDie && !items.some((it) => it.kind !== 'value')) {
    throw new RolagemError('minOneDie', 'a rolagem precisa de pelo menos 1 dado');
  }
  const parsed: ParsedRolagem = { items, sorted, difficulty, message };
  if (rolagemUnits(parsed) > ROLAGEM_LIMITS.maxUnits) throw new RolagemError('flood', 'rolagem grande demais');
  return parsed;
}

/** Tamanho da rolagem para a proteção contra flood: cada dado conta 1, cada termo fixo conta 1. */
export function rolagemUnits(parsed: ParsedRolagem): number {
  return parsed.items.reduce((n, it) => n + (it.kind === 'value' ? 1 : it.count), 0);
}

/**
 * Os índices que ficam de fora com `a`/`b`: os mais baixos (ou os mais altos). No empate, sai o
 * que veio primeiro. Sem nada de fora, `{}`: o item fica igual ao de uma rolagem sem `a`/`b`.
 */
function droppedOf(results: readonly number[], keep: RolagemKeep | undefined): { dropped?: number[] } {
  const out = keep ? results.length - keep.count : 0;
  if (out <= 0) return {};
  const order = results.map((_, i) => i).sort((i, j) => (keep!.which === 'high' ? results[i]! - results[j]! : results[j]! - results[i]!) || i - j);
  return { dropped: order.slice(0, out).sort((i, j) => i - j) };
}

/** Os resultados que contam (sem os que ficaram de fora). */
const countedResults = (it: Extract<RolledItem, { results: unknown }>): number[] =>
  (it.results as number[]).filter((_, i) => !it.dropped?.includes(i));

export function rollRolagem(parsed: ParsedRolagem, rng: Rng): RolagemResult {
  const items: RolledItem[] = parsed.items.map((it) => {
    if (it.kind === 'value') return it;
    if (it.kind === 'fudge') {
      const results = Array.from({ length: it.count }, () => (rng(3) - 1) as -1 | 0 | 1);
      if (parsed.sorted) results.sort((a, b) => a - b);
      return { kind: 'fudge', sign: it.sign, results, ...droppedOf(results, it.keep) };
    }
    const results = Array.from({ length: it.count }, () => rng(it.faces) + 1);
    if (parsed.sorted) results.sort((a, b) => a - b);
    return { kind: 'dice', sign: it.sign, faces: it.faces, results, ...droppedOf(results, it.keep) };
  });

  const total = items.reduce((sum, it) => {
    if (it.kind === 'value') return sum + it.sign * it.value;
    return sum + it.sign * countedResults(it).reduce((a, b) => a + b, 0);
  }, 0);

  let storyteller: RolagemResult['storyteller'] = null;
  if (parsed.difficulty !== null) {
    const k = parsed.difficulty;
    let successes = 0;
    let raw = 0;
    for (const it of items) {
      if (it.kind === 'dice') {
        for (const r of countedResults(it)) {
          if (r === 1) successes--;
          else if (r >= k) {
            successes++;
            raw++;
          }
        }
      } else if (it.kind === 'fudge') {
        for (const r of countedResults(it)) {
          if (r === -1) successes--;
          else if (r >= k) {
            successes++;
            raw++;
          }
        }
      }
    }
    const outcome: StorytellerOutcome = successes > 0 ? 'success' : successes === 0 || raw > 0 ? 'failure' : 'criticalFailure';
    storyteller = { successes, outcome };
  }
  return { parsed, items, total, storyteller };
}

export function rolar(text: string, rng: Rng): RolagemResult {
  return rollRolagem(parseRolagem(text), rng);
}

/** Forma canônica da expressão (sem a mensagem), ex.: "ord 2d6 + 1d4 - 2 > 7", "4d6a3". */
export function formatRolagem(parsed: ParsedRolagem): string {
  const parts: string[] = [];
  parsed.items.forEach((it, i) => {
    const sign = it.sign < 0 ? '- ' : i > 0 ? '+ ' : '';
    const keep = it.kind !== 'value' && it.keep ? `${it.keep.which === 'high' ? 'a' : 'b'}${it.keep.count}` : '';
    const body = it.kind === 'value' ? String(it.value) : it.kind === 'fudge' ? `${it.count}dF${keep}` : `${it.count}d${it.faces}${keep}`;
    parts.push(sign + body);
  });
  let s = (parsed.sorted ? 'ord ' : '') + parts.join(' ');
  if (parsed.difficulty !== null) s += ` > ${parsed.difficulty}`;
  return s;
}

/** Texto dos resultados, ex.: "[3, 5] + 2", "[+, -, 0]" ou "[(2), 5, 4, 4]" (o 2 ficou de fora). */
export function formatRolledItems(items: RolledItem[]): string {
  return items
    .map((it, i) => {
      const sign = it.sign < 0 ? '- ' : i > 0 ? '+ ' : '';
      if (it.kind === 'value') return sign + it.value;
      const shown = it.kind === 'fudge' ? it.results.map((r) => (r > 0 ? '+' : r < 0 ? '-' : '0')) : it.results.map(String);
      return `${sign}[${shown.map((v, j) => (it.dropped?.includes(j) ? `(${v})` : v)).join(', ')}]`;
    })
    .join(' ');
}
